// worktree-bound-tool-rules.ts
//
// Phase 06g R1b (security audit 2026-10-12 C1b): adapter rules name the
// mission worktree by placeholder (`Bash(git -C {{WORKTREE}} status*)`) because
// the path is only known at dispatch. Measured on claude 2.1.293: in
// `Bash(git -C * status*)` the `*` spans whitespace, so `git -C /tmp status`
// and `git -C . -c core.fsmonitor=/usr/bin/true status` both passed. Binding
// the literal path closes both at the permission layer; the pack guard still
// denies `-c`/`--git-dir`/… and a `-C` outside the worktree (R1c).
//
// Pure: session-runner.ts calls this ONCE right before writeBinding and feeds
// the same bound list to the binding file and to `--allowedTools`; the prompt
// section binds the same way so the worker is told the exact rule it has.
import { realpathSync } from "node:fs";
import { resolve } from "node:path";

export const WORKTREE_PLACEHOLDER = "{{WORKTREE}}";
/** Replaced with `realpath(git rev-parse --git-common-dir)` of the source repo at write time. */
export const SOURCE_GIT_DIR_PLACEHOLDER = "{{SOURCE_GIT_DIR}}";
/** Re-audit 06g N7: `<ws>/.avatar/missions/<id>/runtime` — journal, effective-config, active-worker. */
export const CONTROL_ROOT_PLACEHOLDER = "{{CONTROL_ROOT}}";
/** Re-audit 06g N7: the workspace root — `<ws>/.mattermost/credentials.json` holds the bot token. */
export const WORKSPACE_ROOT_PLACEHOLDER = "{{WORKSPACE_ROOT}}";
/** Re-audit 06g N7: the source checkout (main worktree) — its untracked `.env` files. */
export const SOURCE_REPO_PLACEHOLDER = "{{SOURCE_REPO}}";
const PLACEHOLDER_RE = /\{\{[A-Z_]+\}\}/;

export interface WorktreeBindingPaths {
  repoRoot: string;
  /** Only needed for rules carrying the matching placeholder (the settings deny list). */
  sourceGitDir?: string;
  controlRoot?: string;
  workspaceRoot?: string;
  sourceRepo?: string;
}

/**
 * The one spelling of the worktree path every literal rule AND the prompt use:
 * resolved + realpath (macOS `/tmp` ↔ `/private/tmp`), falling back to the
 * resolved path when it does not exist yet (unit tests, fresh init).
 */
export function canonicalWorktreePath(path: string): string {
  const abs = resolve(path);
  try {
    return realpathSync(abs);
  } catch {
    return abs;
  }
}

/** Replaces every placeholder; throws if one would survive into argv/settings. */
export function bindToolRulesToWorktree(
  rules: readonly string[],
  paths: WorktreeBindingPaths,
): string[] {
  const table: Array<[string, string | undefined]> = [
    [WORKTREE_PLACEHOLDER, canonicalWorktreePath(paths.repoRoot)],
    [SOURCE_GIT_DIR_PLACEHOLDER, paths.sourceGitDir],
    [CONTROL_ROOT_PLACEHOLDER, paths.controlRoot],
    [WORKSPACE_ROOT_PLACEHOLDER, paths.workspaceRoot],
    [SOURCE_REPO_PLACEHOLDER, paths.sourceRepo],
  ];
  const bound = rules.map((rule) =>
    table.reduce(
      (out, [placeholder, value]) =>
        value === undefined ? out : out.split(placeholder).join(value),
      rule,
    ),
  );
  const leftover = bound.find((rule) => PLACEHOLDER_RE.test(rule));
  if (leftover !== undefined) {
    throw new Error(
      `worktree-bound-tool-rules: rule "${leftover}" còn placeholder chưa bind — từ chối ghi binding/argv.`,
    );
  }
  return bound;
}
